import { Injectable } from '@angular/core';
import { fiGetMediaBankFileIcon, isDefined } from '@fi-shared/utils';
import { FiImageSize, FileType } from '@fi-shared/types';
import { FiMediaBankService } from '@fi-shared/media-bank/media-bank.service';
import { FiMediaStorageService, IMediaFileUrlArgs } from '@fi-shared/media-bank/fi-media-storage.service';
import { MediaBankStorageType } from '@fi-shared/media-bank/media-bank-storage-type';
import { AppTextService } from '@fi-shared/app-text';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { FiLogErrorService } from '@fi-shared/fi-shared-services/fi-log-error.service';
import { MobileAppHelperBaseService } from '@fi-shared/fi-shared-services/mobile-app-helper-base.service';
import { SaveChangesWarningService } from '@fi-shared/fi-shared-services/save-changes-warning.service';
import { IMediaBankFile, IMediaBankFileReference } from '@fi-shared/media-bank/media-bank';
import { MediaViewerService } from '@fi-shared/media-viewer/media-viewer.service';
import { StatisticService } from '@fi-shared/statistic/statistic.service';
import { INewsFeedContent } from '@fi-shared/fi-news-feed-shared/fi-news-feed-shared';
import { IInsertMediaUploadItem } from '@fi-shared/fi-insert-media/fi-insert-media';
import { FiPrintDownloadService } from '@fi-shared/fi-print-download/fi-print-download.service';

const kiloByte = 1024,
    megaByte = 1024 * kiloByte,
    gigaByte = 1024 * megaByte;

const rc4_key = 'fi-dl';

export interface IDownloadableFile {
    Id?: number;
    Name: string;
    Extension: string;
    ContentType?: string;
    PhysicalFileName: string;
    StorageType?: MediaBankStorageType;
    FileType?: number;
    FullName?: string;
}

@Injectable()
export class MediaBankHelperService {
    constructor(
        private mobileAppService: MobileAppHelperBaseService,
        private mediaStorageService: FiMediaStorageService,
        private fiPrintDownloadService: FiPrintDownloadService,
        private mediaBankService: FiMediaBankService,
        private appTextService: AppTextService,
        private fiDialogsService: FiDialogsService,
        private logErrorService: FiLogErrorService,
        private mediaViewerService: MediaViewerService,
        private statisticService: StatisticService,
        private saveChangesWarningService: SaveChangesWarningService,
    ) {}

    downloadMediaBankFileRef(mediaBankFileReference: IMediaBankFileReference, args?: IMediaFileUrlArgs): Promise<void> {
        const downloadableFile: IDownloadableFile = {
            Id: mediaBankFileReference.MediaBankFileId,
            Name: mediaBankFileReference.Name,
            Extension: mediaBankFileReference.Extension,
            ContentType: mediaBankFileReference.ContentType,
            PhysicalFileName: mediaBankFileReference.PhysicalFileName,
            StorageType: mediaBankFileReference.StorageType,
            FileType: mediaBankFileReference.FileType,
            FullName: mediaBankFileReference.FullName,
        };
        return this.downloadMediaBankFile(downloadableFile, args);
    }

    downloadMediaBankFile(file: IDownloadableFile, args?: IMediaFileUrlArgs): Promise<void> {
        if (!args) {
            args = {
                //openPdfInNewWindow: true,
            };
        }

        const promise = new Promise<void>((resolve, reject) => {
            if (file.FileType == FileType.Pdf) {
                const url = this.mediaStorageService.getMediaFileUrl(file, {
                    size: FiImageSize.Original,
                    contentDisposition: args.openPdfInNewWindow ? 'inline' : 'attachment',
                });
                if (args.openPdfInNewWindow) {
                    this.openNewWindow(url);
                    resolve();
                } else {
                    this.fiPrintDownloadService
                        .downloadBinaryFile(url, {
                            getMethod: true,
                            absolutePath: true,
                            fileName: file.Name + file.Extension,
                            size: FiImageSize.Original,
                            allowMultipleDownloads: args.allowMultipleDownloads,
                        })
                        .then(resolve, reject);
                }
                return;
            }

            this.mediaStorageService.getMediaFileUrlForDownload(file).then(
                (downloadItem) => {
                    if (!downloadItem) {
                        resolve();
                        return;
                    }
                    let url = downloadItem.link;
                    if (file.StorageType !== MediaBankStorageType.Vimeo) {
                        const encDate = window.btoa(new Date().getTime().toString());
                        url += (url.indexOf('?') === -1 ? '?' : '&') + 'dlToken=' + encodeURIComponent(encDate);
                    }
                    const fullName = file.Name ? file.Name + file.Extension : file.FullName;
                    if (this.mobileAppService.mobileAppMode) {
                        this.mobileAppService.downloadFile(url, fullName, file.ContentType).then(
                            () => {
                                resolve();
                            },
                            (err) => {
                                reject();
                            },
                        );
                    } else {
                        this.fiPrintDownloadService
                            .downloadBinaryFile(url, {
                                getMethod: true,
                                absolutePath: true,
                                fileName: fullName,
                                size: isDefined(downloadItem.imageSize) ? downloadItem.imageSize : FiImageSize.Original,
                                allowMultipleDownloads: args.allowMultipleDownloads,
                            })
                            .then(resolve, reject);
                    }
                },
                (err) => {
                    reject();
                    this.fiDialogsService.error();
                    this.logErrorService.logError('upload ' + JSON.stringify(err.error || err));
                },
            );
        });

        // https://chainformation.atlassian.net/browse/FI-5104
        promise.then(() => {
            if (file.Id) {
                this.statisticService.saveFileDownloadAction(file.Id);
                //this.mediaBankService.saveMediaBankFileDownloadInfo(file.Id); // todo remove later?
            }
        });

        return promise;
    }

    openNewWindow(href: string) {
        /*const dialogContentData: IImagePreviewData = {
            url: href,
            fileType: FileType.Pdf,
        };
        this.showPreview(dialogContentData);*/
        window.open(href, '_blank');
    }

    getMediaBankFileIcon(fileType: FileType) {
        return `fa-regular ${fiGetMediaBankFileIcon(fileType)}`;
    }

    isShowDisplayAsImage(file: { FileType?: number }) {
        return file && (file.FileType === FileType.Image || file.FileType === FileType.Movie);
    }

    getFileSizeText(fileSize: number) {
        if (fileSize < kiloByte) {
            return fileSize + ' Bytes';
        } else if (fileSize < megaByte) {
            return (fileSize / kiloByte).toFixed(2) + ' KB';
        } else if (fileSize < gigaByte) {
            return (fileSize / megaByte).toFixed(2) + ' MB';
        } else {
            return (fileSize / gigaByte).toFixed(2) + ' GB';
        }
    }

    getMediaFileUrlPreview(item: IInsertMediaUploadItem): string {
        switch (item.mediaBankFile.FileType) {
            case FileType.Image:
            case FileType.Pdf: {
                const imageSize = FiImageSize.Small;
                return this.mediaStorageService.getMediaFileUrl(item.mediaBankFile, { size: imageSize });
            }
        }
    }

    getFileTypeName(fileType: number) {
        switch (fileType) {
            case FileType.Unknown:
                return this.appTextService.get('common.unknown');
            case FileType.Image:
                return this.appTextService.get('common.image');
            case FileType.Movie:
                return this.appTextService.get('common.video');
            default:
                return this.appTextService.get('common.document');
        }
    }

    isImageFileType(fileType: FileType): boolean {
        return fileType === FileType.Image;
    }

    isAzureVideoFile(fileType: FileType, storageType: MediaBankStorageType): boolean {
        return fileType === FileType.Movie && storageType === MediaBankStorageType.Azure;
    }

    isVimeoVideoFile(fileType: FileType, storageType: MediaBankStorageType): boolean {
        return (
            (fileType === FileType.Movie || fileType === FileType.MovieNotPlayable) &&
            storageType === MediaBankStorageType.Vimeo
        );
    }

    showOrDownloadFile(fileId: number) {
        this.mediaBankService.getMediaBankFile(fileId, true).then((data: IMediaBankFile) => {
            const mediaBankFileReference = this.getMediaBankFileReferenceFromMediaFile(data, {}); // todo why need here reference instead file?
            const imageUrl = this.mediaStorageService.getMediaFileUrl(mediaBankFileReference);
            if (this.isImageFileType(data.FileType) || this.isVimeoVideoFile(data.FileType, data.StorageType)) {
                this.mediaViewerService.Show(imageUrl, undefined, [mediaBankFileReference]);
            } else if (this.isAzureVideoFile(data.FileType, data.StorageType)) {
                this.mediaViewerService.ShowVideoPopup(imageUrl, undefined, mediaBankFileReference);
            } else {
                this.downloadMediaBankFileRef(mediaBankFileReference);
            }
        });
    }

    getMediaBankFileReferenceFromNewsItemContent(
        content: INewsFeedContent | IMediaBankFileReference,
        objectId: number,
        name: string,
    ): IMediaBankFileReference {
        return {
            PhysicalFileName: content.PhysicalFileName,
            Extension: content.Extension,
            Name: name,
            FileType: content.FileType,
            StorageType: content.StorageType,
            ContentType: content.ContentType,
            MediaBankFileId: objectId,
            ObjectId: objectId,
        };
    }

    getMediaBankFileReferenceFromMediaFile(
        mediaBankFile: IMediaBankFile,
        args: { isNewUpload?: boolean; showInMediaBank?: boolean; contentLanguageId?: number },
    ): IMediaBankFileReference {
        if (!args) {
            args = {};
        }
        const mbRef: IMediaBankFileReference = {
            MediaBankFileId: mediaBankFile.Id,
            PhysicalFileName: mediaBankFile.PhysicalFileName,
            Name: mediaBankFile.Name,
            Extension: mediaBankFile.Extension,
            FileType: mediaBankFile.FileType,
            StorageType: mediaBankFile.StorageType,
            ContentType: mediaBankFile.ContentType,
            Height: mediaBankFile.Height,
            Width: mediaBankFile.Width,
            Size: mediaBankFile.Size,
            CreatedByUserId: mediaBankFile.CreatedByUserId,
            CreatedByUserName: mediaBankFile.CreatedByUserName,
            CreatedOn: mediaBankFile.CreatedOn,
            ChangedOn: mediaBankFile.ChangedOn,
            FileCreatedOn: mediaBankFile.FileCreatedOn,
            ContentLanguageId: args.contentLanguageId,
            IsNewUpload: !!args.isNewUpload,
            HideFromMediaBank: args.isNewUpload && isDefined(args.showInMediaBank) ? !args.showInMediaBank : false,
        };
        return mbRef;
    }
}
